// src/receptionist/components/BookingDrawer.tsx
//
// A booking, read the way the candidate profile is read: a right-hand drawer
// over the list, not a row that pushes everything below it down the page.
// Same shell as containers/drawer/drawer-candidate-profile.tsx — a scrim, a
// fixed panel that slides in from the right, a header with the close button,
// and a scrolling body with the actions pinned to the foot.

import { useEffect, useState, type ReactNode } from "react";
import { AlertTriangleIcon, CheckCircle2Icon, CircleDashedIcon, Loader2Icon, MessageSquareTextIcon, ReceiptTextIcon, SendIcon, XIcon, ZapIcon } from "lucide-react";
import { Button } from "./kit";
import { Textarea } from "@component/textarea";
import { sendBookingMessage } from "@service/receptionist";
import { FollowUpDialog } from "./BookingDialogs";
import { cn } from "@oura/lib/cn";
import { isImage, isReplyWindowOpen, MEDIA_PLACEHOLDER, slotWhen, type Booking, type Turn } from "../bookings";

const STATUS_STYLE: Record<Booking["status"], string> = {
    Pending: "bg-amber-100 text-amber-700",
    Confirmed: "bg-emerald-100 text-emerald-700",
    Declined: "bg-gray-200 text-gray-600",
    Cancelled: "bg-gray-200 text-gray-600",
};

function TranscriptTurn({ turn }: { turn: Turn }) {
    if (turn.who === "system") {
        return (
            <div className="self-center rounded-md bg-amber-50 px-3 py-1 text-center text-xs text-amber-700">
                {turn.text}
            </div>
        );
    }
    const fromCustomer = turn.who === "customer";
    const media = turn.media ?? [];
    // A turn that was only an attachment is logged as "[media]": with the attachment here
    // to show, the placeholder says nothing (ourteam-frontend#4899).
    const text = media.length && turn.text.trim() === MEDIA_PLACEHOLDER ? "" : turn.text;
    return (
        <div
            className={cn(
                "max-w-[84%] whitespace-pre-line rounded-lg px-3 py-2 text-sm leading-snug shadow-sm",
                fromCustomer ? "self-start bg-white text-gray-900" : "self-end bg-blue-100 text-gray-900",
            )}
        >
            {media.length > 0 && (
                <div className={cn("flex flex-wrap gap-2", text && "mb-2")}>
                    {media.map((m, i) =>
                        isImage(m) ? (
                            <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" className="block">
                                <img
                                    src={m.url}
                                    alt={fromCustomer ? "Photo from the customer" : "Picture sent to the customer"}
                                    loading="lazy"
                                    className="max-h-[220px] max-w-[220px] rounded-md object-cover"
                                />
                            </a>
                        ) : (
                            <a key={i} href={m.url} target="_blank" rel="noopener noreferrer"
                               className="text-blue-600 underline underline-offset-2">
                                Open attachment
                            </a>
                        ),
                    )}
                </div>
            )}
            {text}
            {turn.at && (
                <span className={cn("mt-1 block text-right text-[11px] leading-none", fromCustomer ? "text-gray-400" : "text-blue-700/60")}>
                    {timeOf(turn.at)}
                </span>
            )}
        </div>
    );
}

/** "3:42 PM" in the viewer's clock. */
function timeOf(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-MY", { hour: "numeric", minute: "2-digit" });
}

/** "Today", "Yesterday" or "Thu, 5 Nov" — the separator between days of a thread. */
function dayOf(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((start(new Date()) - start(d)) / 86_400_000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return d.toLocaleDateString("en-MY", { weekday: "short", day: "numeric", month: "short" });
}

/** "a photo of the area of concern" (worded to sit inside a sentence) as a list item. */
function sentenceCase(text: string): string {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}

/** The customer's initials for the header's avatar. */
function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    return (parts.length === 1 ? parts[0].slice(0, 2) : parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** One row of the Details tab: a label on the left, the value beside it. */
function DetailRow({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 border-b border-gray-100 py-3 last:border-b-0 dark:border-gray-800">
            <dt className="text-[13px] leading-5 font-medium text-gray-500">{label}</dt>
            <dd className="m-0 min-w-0 text-sm leading-5 text-gray-900 dark:text-gray-100">{children}</dd>
        </div>
    );
}

/**
 * Everything the request holds, as a list (ourteam-backend#6997): the slot, when and how it
 * came in, what the booking steps asked and what was said, and which documents arrived.
 */
function DetailsTab({ booking }: { booking: Booking }) {
    const when = slotWhen(booking);
    const answers = booking.answers ?? [];
    const documents = booking.documents ?? [];
    const sections = Array.from(new Set(answers.map((a) => a.section)));
    return (
        <dl className="m-0 rounded-xl border border-gray-200 bg-white px-4 dark:border-gray-800 dark:bg-gray-900">
            <DetailRow label="Service">{booking.service || "Not chosen yet"}</DetailRow>
            <DetailRow label="Slot">
                {when ? `${when.date}, ${when.time}` : booking.slot || <span className="text-gray-500">Not given yet</span>}
                {booking.slot && (
                    <span className="mt-0.5 block text-xs text-gray-500">
                        {when ? `Customer said: ${booking.slot}` : "No date yet"}
                    </span>
                )}
            </DetailRow>
            <DetailRow label="Received">
                {booking.createdAt ? `${dayOf(booking.createdAt)}, ${timeOf(booking.createdAt)}` : "—"}
                <span className="text-gray-500"> · {booking.source}</span>
            </DetailRow>
            {sections.map((section) => (
                <DetailRow key={section} label={section}>
                    <ul className="list-plain m-0 space-y-2.5 p-0">
                        {answers.filter((a) => a.section === section).map((a, i) => (
                            <li key={i}>
                                <span className="block text-xs text-gray-500">{a.question}</span>
                                <span className="flex flex-wrap items-center gap-1.5">
                                    {a.answer}
                                    {a.flagged && (
                                        <span className="rounded-full bg-amber-100 px-1.5 py-px text-[11px] font-medium text-amber-700">Flagged</span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </DetailRow>
            ))}
            {documents.length > 0 && (
                <DetailRow label="Documents">
                    <ul className="list-plain m-0 space-y-1.5 p-0">
                        {documents.map((d, i) => (
                            <li key={i} className="flex items-center gap-1.5">
                                {d.received
                                    ? <CheckCircle2Icon className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                                    : <CircleDashedIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />}
                                <span className={d.received ? "" : "text-gray-500"}>{sentenceCase(d.label)}</span>
                                <span className="text-xs text-gray-500">· {d.received ? "received" : "waiting"}</span>
                            </li>
                        ))}
                    </ul>
                </DetailRow>
            )}
            {(booking.tags ?? []).length > 0 && (
                <DetailRow label="Tags">
                    <span className="flex flex-wrap gap-1.5">
                        {booking.tags.map((t) => (
                            <span key={t} className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">{t}</span>
                        ))}
                    </span>
                </DetailRow>
            )}
            {booking.status !== "Pending" && booking.note && <DetailRow label="Outcome">{booking.note}</DetailRow>}
        </dl>
    );
}

/** The thread, with a separator between days (ourteam-backend#6997 sends each message's time). */
function ConversationTab({ booking }: { booking: Booking }) {
    const turns = booking.transcript ?? [];
    let lastDay = "";
    return (
        <div className="flex flex-col gap-1.5">
            {turns.length === 0 && <p className="m-0 text-sm text-gray-500">No messages yet.</p>}
            {turns.map((t, i) => {
                const day = t.at ? dayOf(t.at) : "";
                const separator = day && day !== lastDay;
                if (day) lastDay = day;
                return (
                    <div key={i} className="contents">
                        {separator && (
                            <div className="my-2 flex items-center gap-3 text-[11px] font-medium text-gray-400">
                                <span className="h-px flex-1 bg-gray-200" />{day}<span className="h-px flex-1 bg-gray-200" />
                            </div>
                        )}
                        <TranscriptTurn turn={t} />
                    </div>
                );
            })}
        </div>
    );
}

type DrawerTab = "details" | "conversation";

/**
 * A reply on the customer's own thread, sized to WhatsApp's rule rather than
 * ours: outside the 24-hour window nothing free-form is delivered, so the box
 * stays visible but disabled with the reason said out loud — the same shape as
 * the candidate composer (containers/drawer/tabs/whatsapp-reply-composer.tsx).
 * The server's refusal is still the authority; this is only the early warning.
 */
function Composer({
    booking,
    onSent,
    trailingAction,
}: {
    booking: Booking;
    onSent: (b: Booking) => void;
    /** Rendered beside Send, and beside the blocked banner too. */
    trailingAction?: ReactNode;
}) {
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [blocked, setBlocked] = useState<string | null>(null);

    // A different booking is a different thread; nothing carries over.
    useEffect(() => { setDraft(""); setError(null); setBlocked(null); }, [booking.id]);

    const windowOpen = isReplyWindowOpen(booking);
    const disabled = sending || !windowOpen || blocked !== null;

    const send = async () => {
        const text = draft.trim();
        if (!text || disabled) return;
        setSending(true);
        setError(null);
        try {
            const result = await sendBookingMessage(booking.id, { text });
            if (result.ok) { setDraft(""); onSent(result.booking); return; }
            if (result.kind === "window_closed") { setBlocked(result.message); return; }
            setError(result.message);
        } finally {
            setSending(false);
        }
    };

    if (blocked) {
        return (
            <div className="flex items-start gap-2">
                <div className="flex flex-1 items-start gap-2 rounded-xl bg-amber-50 ring-1 ring-inset ring-amber-200 px-3 py-2">
                    <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-700">{blocked}</p>
                </div>
                {trailingAction}
            </div>
        );
    }

    return (
        <div>
            {!windowOpen && (
                <p className="mb-1.5 text-[11px] text-gray-400">
                    You can't reply right now — WhatsApp only delivers replies within 24 hours of the
                    customer's last message.
                </p>
            )}
            {error && <p className="mb-1.5 text-xs text-rose-600">{error}</p>}
            <div className="flex items-end gap-2">
                <Textarea
                    rows={1}
                    value={draft}
                    disabled={disabled}
                    placeholder="Reply to the customer on WhatsApp…"
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
                    }}
                    className="max-h-32 min-h-0 flex-1 resize-none rounded-lg border-gray-300 bg-white px-3 py-2 text-sm shadow-none placeholder:text-gray-400 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900"
                />
                <Button
                    size="icon"
                    onClick={() => void send()}
                    disabled={!draft.trim() || disabled}
                    aria-label="Send reply"
                    title={windowOpen ? "Send reply" : "The 24-hour reply window has closed"}
                    className="flex-shrink-0"
                >
                    {sending ? <Loader2Icon className="h-4 w-4 animate-spin" /> : <SendIcon className="h-4 w-4" />}
                </Button>
                {trailingAction}
            </div>
        </div>
    );
}

export function BookingDrawer({
    booking,
    onClose,
    onDecline,
    onStale,
    onSent,
    merchantId,
    profileHref,
}: {
    /** null keeps the panel mounted but off-screen, so it slides rather than pops. */
    booking: Booking | null;
    onClose: () => void;
    /** The server's booking after a decline lands, so the row and thread redraw. */
    onDecline: (booking: Booking) => void;
    /** The staff group decided first; the list needs refetching. */
    onStale: (message: string) => void;
    /** Whose template pack the follow-up dialog offers. */
    merchantId: string | null;
    /** The server's booking after a reply lands, so the thread redraws. */
    onSent: (booking: Booking) => void;
    profileHref: string;
}) {
    const open = booking !== null;

    // Escape closes it, and the page behind does not scroll while it is open —
    // both are what the candidate drawer does.
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    const [followUpOpen, setFollowUpOpen] = useState(false);
    const [tab, setTab] = useState<DrawerTab>("details");

    // A different booking is a different decision; the dialog does not carry over, and it opens on Details.
    useEffect(() => { setFollowUpOpen(false); setTab("details"); }, [booking?.id]);

    return (
        <>
            <div
                onClick={onClose}
                aria-hidden
                className={cn(
                    "fixed inset-0 z-40 bg-black/30 transition-opacity duration-300",
                    open ? "opacity-100" : "pointer-events-none opacity-0",
                )}
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={booking ? `Booking for ${booking.customer}` : "Booking"}
                className={cn(
                    "fixed inset-y-0 right-0 z-50 flex w-full transform flex-col border-l bg-white transition-transform duration-300 ease-in-out sm:w-[600px] md:w-[700px] lg:w-1/2 dark:bg-gray-900",
                    open ? "translate-x-0" : "translate-x-full",
                )}
            >
                {booking && (
                    <>
                        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
                            <div className="flex min-w-0 items-center gap-3">
                                <span aria-hidden className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-full bg-blue-50 text-sm font-semibold text-blue-700 ring-1 ring-inset ring-blue-100">
                                    {initials(booking.customer)}
                                </span>
                                <div className="min-w-0">
                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                        <h2 className="m-0 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">{booking.customer}</h2>
                                        <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", STATUS_STYLE[booking.status] ?? "bg-gray-200 text-gray-600")}>
                                            {booking.status}
                                        </span>
                                    </div>
                                    <p className="m-0 text-sm text-gray-500">{booking.phone}{booking.service ? ` · ${booking.service}` : ""}</p>
                                </div>
                            </div>
                            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close" className="h-8 w-8 flex-shrink-0">
                                <XIcon className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* Details / Conversation (ourteam-backend#6997), underlined like the page tabs. */}
                        <div role="tablist" aria-label="Booking" className="flex gap-1 border-b border-gray-200 px-5 dark:border-gray-800">
                            {([
                                { value: "details", label: "Details", icon: ReceiptTextIcon },
                                { value: "conversation", label: "Conversation", icon: MessageSquareTextIcon, count: (booking.transcript ?? []).length },
                            ] as const).map(({ value, label, icon: Icon, ...rest }) => {
                                const on = tab === value;
                                const count = "count" in rest ? rest.count : undefined;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        role="tab"
                                        aria-selected={on}
                                        onClick={() => setTab(value)}
                                        className={cn(
                                            "-mb-px flex items-center gap-1.5 border-b-2 px-3 pb-2.5 pt-1 text-sm font-medium transition-colors",
                                            on ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-900",
                                        )}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {label}
                                        {count !== undefined && <span className="tabular-nums text-gray-400">{count}</span>}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto bg-gray-50 p-5 dark:bg-gray-950">
                            {tab === "details" ? <DetailsTab booking={booking} /> : <ConversationTab booking={booking} />}
                        </div>

                        <div className="border-t border-gray-200 p-4 dark:border-gray-800">
                            <Composer
                                booking={booking}
                                onSent={onSent}
                                trailingAction={
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-10 flex-shrink-0 gap-1.5 text-xs"
                                        onClick={() => setFollowUpOpen(true)}
                                    >
                                        <ZapIcon className="h-3.5 w-3.5" />
                                        Follow up
                                    </Button>
                                }
                            />
                        </div>
                        <FollowUpDialog
                            open={followUpOpen}
                            onOpenChange={setFollowUpOpen}
                            booking={booking}
                            merchantId={merchantId}
                            profileHref={profileHref}
                            onSent={onSent}
                            onDeclined={onDecline}
                            onStale={onStale}
                        />
                    </>
                )}
            </div>
        </>
    );
}
